import { defineStore } from 'pinia'
import {
  ADMIN_RESTAURANTS_PAGE_SIZE,
  fetchAdminRestaurantById,
  fetchAdminRestaurants,
} from '@/entities/admin/api/adminRestaurantsApi'

const RESTAURANT_OPTIONS_PAGE_SIZE = 200

export const useAdminRestaurantsStore = defineStore('adminRestaurants', {
  state: () => ({
    selectedRestaurantId: null,
    activeRestaurant: null,
    isActiveRestaurantLoading: false,
    activeRestaurantError: null,
    restaurantList: [],
    restaurantListTotal: 0,
    isRestaurantListLoading: false,
    restaurantListError: null,
    hasLoadedRestaurantList: false,
    allRestaurants: [],
    isAllRestaurantsLoading: false,
    allRestaurantsError: null,
    hasLoadedAllRestaurants: false,
    pageIndex: 0,
    pageSize: ADMIN_RESTAURANTS_PAGE_SIZE,
    sortField: 'title',
    sortOrder: 1,
    filters: {
      restaurantId: '',
      title: '',
      cuisineType: '',
      tag: '',
      isActive: '',
      status: '',
    },
  }),
  getters: {
    restaurantOptions: (state) =>
      state.allRestaurants.map((restaurant) => ({
        label: restaurant.title,
        value: restaurant.id,
      })),
  },
  actions: {
    clear() {
      this.selectedRestaurantId = null
      this.activeRestaurant = null
      this.isActiveRestaurantLoading = false
      this.activeRestaurantError = null
      this.restaurantList = []
      this.restaurantListTotal = 0
      this.isRestaurantListLoading = false
      this.restaurantListError = null
      this.hasLoadedRestaurantList = false
      this.allRestaurants = []
      this.isAllRestaurantsLoading = false
      this.allRestaurantsError = null
      this.hasLoadedAllRestaurants = false
      this.pageIndex = 0
      this.pageSize = ADMIN_RESTAURANTS_PAGE_SIZE
      this.sortField = 'title'
      this.sortOrder = 1
      this.filters = {
        restaurantId: '',
        title: '',
        cuisineType: '',
        tag: '',
        isActive: '',
        status: '',
      }
    },
    setSelectedRestaurantId(restaurantId) {
      this.selectedRestaurantId = restaurantId ? String(restaurantId) : null
    },
    mergeRestaurantInList(restaurant) {
      if (!restaurant?.id) {
        return
      }
      const index = this.restaurantList.findIndex((item) => item.id === restaurant.id)
      if (index >= 0) {
        this.restaurantList[index] = { ...this.restaurantList[index], ...restaurant }
      }
    },
    setFilters({
      restaurantId = '',
      title = '',
      cuisineType = '',
      tag = '',
      isActive = '',
      status = '',
    } = {}) {
      this.filters = {
        restaurantId: String(restaurantId ?? '').trim(),
        title: String(title ?? '').trim(),
        cuisineType: String(cuisineType ?? '').trim(),
        tag: String(tag ?? '').trim(),
        isActive: isActive === true || isActive === false ? isActive : '',
        status: status === 'active' || status === 'blocked' ? status : '',
      }
      this.pageIndex = 0
    },
    setPagination({ pageIndex = 0, pageSize = ADMIN_RESTAURANTS_PAGE_SIZE } = {}) {
      this.pageIndex = Math.max(0, Number(pageIndex) || 0)
      this.pageSize = Math.max(1, Number(pageSize) || ADMIN_RESTAURANTS_PAGE_SIZE)
    },
    setSorting({ sortField = 'title', sortOrder = 1 } = {}) {
      this.sortField = sortField || 'title'
      this.sortOrder = Number(sortOrder) === -1 ? -1 : 1
      this.pageIndex = 0
    },
    async loadRestaurantsPage() {
      const filter = {}
      if (this.filters.restaurantId) {
        filter._id = this.filters.restaurantId
      }
      if (this.filters.title) {
        const escaped = String(this.filters.title).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        filter.title = {
          $regex: escaped,
          $options: 'i',
        }
      }
      if (this.filters.cuisineType) {
        filter.cuisine_type = this.filters.cuisineType
      }
      if (this.filters.tag) {
        filter.tags = { $in: [this.filters.tag] }
      }
      if (this.filters.isActive === true || this.filters.isActive === false) {
        filter.is_active = this.filters.isActive
      }
      if (this.filters.status === 'active' || this.filters.status === 'blocked') {
        filter.status = this.filters.status
      }

      const { items, total } = await fetchAdminRestaurants({
        limit: this.pageSize,
        skip: this.pageIndex * this.pageSize,
        filter,
        sort: { [this.sortField]: this.sortOrder },
      })

      this.restaurantList = items
      this.restaurantListTotal = total
      this.hasLoadedRestaurantList = true
    },
    async fetchRestaurantList({ force = false } = {}) {
      if (this.isRestaurantListLoading || (this.hasLoadedRestaurantList && !force)) {
        return
      }

      this.isRestaurantListLoading = true
      this.restaurantListError = null
      try {
        await this.loadRestaurantsPage()
      } catch (error) {
        this.restaurantListError = error
      } finally {
        this.isRestaurantListLoading = false
      }
    },
    async fetchAllRestaurants({ force = false } = {}) {
      if (this.isAllRestaurantsLoading || (this.hasLoadedAllRestaurants && !force)) {
        return
      }

      this.isAllRestaurantsLoading = true
      this.allRestaurantsError = null
      try {
        const { items } = await fetchAdminRestaurants({
          limit: RESTAURANT_OPTIONS_PAGE_SIZE,
          sort: { title: 1 },
        })
        this.allRestaurants = items
        this.hasLoadedAllRestaurants = true
      } catch (error) {
        this.allRestaurantsError = error
        this.allRestaurants = []
      } finally {
        this.isAllRestaurantsLoading = false
      }
    },
    async loadSelectedRestaurant(restaurantId) {
      const id = restaurantId ?? this.selectedRestaurantId
      if (!id) {
        this.activeRestaurant = null
        return
      }

      if (String(id) !== String(this.selectedRestaurantId)) {
        this.setSelectedRestaurantId(id)
      }

      this.isActiveRestaurantLoading = true
      this.activeRestaurantError = null
      try {
        const restaurant = await fetchAdminRestaurantById(id)
        this.activeRestaurant = restaurant
        if (restaurant) {
          this.mergeRestaurantInList(restaurant)
        }
      } catch (error) {
        this.activeRestaurantError = error
        this.activeRestaurant = null
      } finally {
        this.isActiveRestaurantLoading = false
      }
    },
  },
})
